/**
 * =============================================================================
 * PATIENT JOURNEY - STEP CARD
 * =============================================================================
 *
 * One card in the vertical stepper. It handles the three states a step can be
 * in and always shows the step name, its status and its timestamp:
 *
 *   completed -> locked summary of what was recorded
 *   active    -> header plus the interactive body passed as children
 *   upcoming  -> locked placeholder, no data and no actions
 * =============================================================================
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import moment from "moment";

import { getEntryFieldSummary } from "./journeyConfig";

import {
    FONT,
    JOURNEY_COLORS,
    cardSx,
    chipSx,
    stepCaptionSx,
    stepTitleSx,
} from "./journeyStyles";

export const STEP_CARD_VARIANT = {
    COMPLETED: "completed",
    ACTIVE: "active",
    UPCOMING: "upcoming",
};

/* -----------------------------------------------------------------------------
   HELPERS
----------------------------------------------------------------------------- */

const formatDate = (value) => {
    if (!value) {
        return null;
    }

    const parsed = moment(value);

    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : null;
};

const formatDateTime = (value) => {
    if (!value) {
        return null;
    }

    const parsed = moment(value);

    return parsed.isValid()
        ? parsed.format("DD/MM/YYYY hh:mm A")
        : null;
};

const getBorderColor = (variant) => {
    if (variant === STEP_CARD_VARIANT.COMPLETED) {
        return JOURNEY_COLORS.success;
    }

    if (variant === STEP_CARD_VARIANT.ACTIVE) {
        return JOURNEY_COLORS.accent;
    }

    return JOURNEY_COLORS.border;
};

/* -----------------------------------------------------------------------------
   COMPONENT
----------------------------------------------------------------------------- */

const JourneyStepCard = ({
    variant = STEP_CARD_VARIANT.UPCOMING,
    stepLabel = "",
    title = "",
    location = null,
    entry = null,
    children = null,
}) => {
    const isCompleted = variant === STEP_CARD_VARIANT.COMPLETED;
    const isActive = variant === STEP_CARD_VARIANT.ACTIVE;
    const isUpcoming = variant === STEP_CARD_VARIANT.UPCOMING;
    const formValues = getEntryFieldSummary(entry);

    /**
     * "Recorded by Dr. Priya Sharma | 19/07/2026" - the outcome time when the
     * step captured one, otherwise the time the step was saved.
     */
    const metaLine = [
        entry?.recordedBy ? "Recorded by " + entry.recordedBy : "Recorded",
        formatDate(entry?.recordedAt),
    ]
        .filter(Boolean)
        .join(" | ");

    return (
        <Box
            sx={cardSx(getBorderColor(variant))}
        >
            <Box
                sx={{
                    padding: isUpcoming
                        ? { xs: "10px 12px", sm: "12px 16px" }
                        : { xs: "12px 12px", sm: "14px 16px" },
                    boxSizing: "border-box",
                }}
            >
                {/* ============ HEADER ============ */}

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: { xs: "8px", sm: "12px" },
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={stepCaptionSx}>
                            {stepLabel}
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "8px",
                                marginTop: "2px",
                            }}
                        >
                            <Typography
                                sx={{
                                    ...stepTitleSx,
                                    color: isUpcoming
                                        ? JOURNEY_COLORS.textMuted
                                        : JOURNEY_COLORS.text,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {title}
                            </Typography>

                            {location && (
                                <Box
                                    sx={chipSx(
                                        isUpcoming
                                            ? JOURNEY_COLORS.textMuted
                                            : JOURNEY_COLORS.accent,
                                        isUpcoming
                                            ? JOURNEY_COLORS.border
                                            : JOURNEY_COLORS.accentSoft
                                    )}
                                >
                                    {location}
                                </Box>
                            )}

                        </Box>
                    </Box>

                    {/* STATUS ICON */}

                    {isCompleted && (
                        <CheckCircleIcon
                            sx={{
                                fontSize: "22px",
                                color: JOURNEY_COLORS.success,
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>

                {/* ============ COMPLETED SUMMARY ============ */}

                {isCompleted && (
                    <Box sx={{ marginTop: "4px" }}>
                        {entry?.outcomeLabel && (
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "12px",
                                    lineHeight: "18px",
                                    fontWeight: 600,
                                    color: "#348530",
                                }}
                            >
                                {entry.outcomeLabel}
                                {entry?.timestamp && (
                                    <>
                                        {" | "}
                                        {formatDateTime(entry.timestamp)}
                                    </>
                                )}
                            </Typography>
                        )}

                        {formValues.length > 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    columnGap: { xs: "16px", sm: "24px" },
                                    rowGap: "8px",
                                    marginTop: "6px",
                                }}
                            >
                                {formValues.map((detail) => (
                                    <Box key={detail.name}>
                                        <Typography
                                            sx={{
                                                ...stepCaptionSx,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {detail.label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontFamily: FONT,
                                                fontSize: "12px",
                                                lineHeight: "18px",
                                                fontWeight: 600,
                                                color: JOURNEY_COLORS.text,
                                            }}
                                        >
                                            {detail.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* "Recorded by Dr. Priya Sharma | 19/07/2026" */}

                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "11px",
                                lineHeight: "16px",
                                fontWeight: 400,
                                color: JOURNEY_COLORS.textMuted,
                                marginTop: "3px",
                            }}
                        >
                            {metaLine}
                        </Typography>
                    </Box>
                )}

                {/* ============ ACTIVE BODY ============ */}

                {isActive && children}
            </Box>
        </Box>
    );
};

export default JourneyStepCard;
